import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiUsers, FiMapPin, FiActivity, FiBriefcase, FiTrendingUp, FiUserPlus, FiClock, FiFileText, FiAlertCircle } from '../utils/icons';
import { getDashboardStats, getAllEmployees, getOrganizations } from '../services/adminService';
import { DashboardStats, Employee, Organization } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 8px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  font-weight: 400;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TimeFilter = styled.div`
  display: flex;
  gap: 8px;
  background: ${({ theme }) => theme.colors.surface};
  padding: 4px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.text.secondary};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primaryDark : theme.colors.surfaceLight};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCardNew = styled.div<{ gradient?: string }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const StatCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const StatIconWrapper = styled.div<{ bgColor?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ bgColor }) => bgColor || '#667eea'}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ bgColor }) => bgColor || '#667eea'};
  font-size: 22px;
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  letter-spacing: -1px;
`;

const StatTrend = styled.div<{ positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ positive }) => positive ? '#10b981' : '#ef4444'};
  margin-top: 12px;

  svg {
    font-size: 14px;
  }
`;

const Section = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: -0.3px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
`;

const ErrorMessage = styled.div`
  padding: 16px 20px;
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 12px;
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  font-size: 14px;
`;

const QuickAccessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const QuickAccessCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  border: 1px solid ${({ theme }) => theme.colors.divider};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);

    .icon-wrapper {
      background: ${({ theme }) => theme.colors.primary};
      color: white;
    }
  }

  .icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.background};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 18px;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .card-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .card-title {
    font-weight: 600;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .card-subtitle {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

const InsightCard = styled.div<{ warning?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${({ theme, warning }) => warning ? theme.colors.error : theme.colors.divider};
  border-left: 4px solid ${({ theme, warning }) => warning ? theme.colors.error : theme.colors.primary};

  .insight-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    svg {
      color: ${({ theme, warning }) => warning ? theme.colors.error : theme.colors.primary};
      font-size: 18px;
    }
  }

  .insight-title {
    font-weight: 600;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .insight-description {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.5;
  }
`;

const ActivityFeed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};

  .activity-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primary}15;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 14px;
    flex-shrink: 0;
  }

  .activity-content {
    flex: 1;
    min-width: 0;
  }

  .activity-title {
    font-weight: 600;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 2px;
  }

  .activity-description {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .activity-time {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState('week');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);

      // Fetch first page to get total count
      const [statsResponse, firstEmployeesResponse, orgsResponse] = await Promise.all([
        getDashboardStats(),
        getAllEmployees(undefined, { page: 1, limit: 100 }),
        getOrganizations(undefined, { page: 1, limit: 100 })
      ]);

      if (statsResponse.success && statsResponse.data) {
        console.log('===== DASHBOARD STATS =====');
        console.log('Stats response:', statsResponse.data);
        setStats(statsResponse.data);
      } else {
        console.error('Failed to get dashboard stats:', statsResponse);
      }

      // Fetch all employees if there are more than 100
      let allEmployees = firstEmployeesResponse.data || [];
      if (firstEmployeesResponse.success && firstEmployeesResponse.pagination) {
        const totalPages = firstEmployeesResponse.pagination.totalPages || 1;

        // Fetch remaining pages if there are more
        if (totalPages > 1) {
          const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
          const remainingResponses = await Promise.all(
            remainingPages.map(page => getAllEmployees(undefined, { page, limit: 100 }))
          );

          remainingResponses.forEach(response => {
            if (response.success && response.data) {
              allEmployees = [...allEmployees, ...response.data];
            }
          });
        }

        console.log('===== EMPLOYEES DATA =====');
        console.log('Total employees:', allEmployees.length);
        console.log('First 10 employees with dates:', allEmployees.slice(0, 10).map(e => ({
          name: e.name,
          createdAt: e.createdAt,
          createdAtType: typeof e.createdAt,
          parsed: new Date(e.createdAt).toISOString()
        })));
        setEmployees(allEmployees);
      } else {
        console.error('Failed to get employees:', firstEmployeesResponse);
      }

      if (orgsResponse.success && orgsResponse.data) {
        console.log('===== ORGANIZATIONS DATA =====');
        console.log('Total organizations fetched:', orgsResponse.data.length);
        console.log('Organizations pagination:', orgsResponse.pagination);
        setOrganizations(orgsResponse.data);
      } else {
        console.error('Failed to get organizations:', orgsResponse);
      }
    } catch (err) {
      setError('An error occurred while loading dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingMessage>Loading dashboard...</LoadingMessage>;
  }

  if (error) {
    return (
      <DashboardContainer>
        <PageHeader>
          <HeaderLeft>
            <PageTitle>Dashboard</PageTitle>
            <PageSubtitle>Welcome to CRESFMS Admin Panel</PageSubtitle>
          </HeaderLeft>
        </PageHeader>
        <ErrorMessage>{error}</ErrorMessage>
      </DashboardContainer>
    );
  }

  // Filter employees based on time filter
  const getFilteredEmployees = () => {
    const now = new Date();
    let cutoffDate = new Date();

    switch (timeFilter) {
      case 'day':
        cutoffDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        cutoffDate.setDate(cutoffDate.getDate() - 7);
        break;
      case 'month':
        cutoffDate.setMonth(cutoffDate.getMonth() - 1);
        break;
      case 'year':
        cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
        break;
      default:
        cutoffDate.setDate(cutoffDate.getDate() - 7);
    }

    return employees.filter(e => new Date(e.createdAt) >= cutoffDate);
  };

  const filteredEmployees = getFilteredEmployees();

  // Calculate employee statistics from filtered data
  const activeEmployees = filteredEmployees.filter(e => e.status === 'active').length;
  const inactiveEmployees = filteredEmployees.filter(e => e.status === 'inactive').length;
  const terminatedEmployees = filteredEmployees.filter(e => e.status === 'terminated').length;

  // For "new this week" we always show the last 7 days regardless of filter
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const employeesThisWeek = employees.filter(e => new Date(e.createdAt) >= oneWeekAgo).length;

  // Calculate employees without documents (from all employees, not filtered)
  const employeesWithoutDocs = employees.filter(e => !e.documents ||
    Object.keys(e.documents).length === 0).length;

  // Calculate employees in probation (from all employees, not filtered)
  const now = new Date();
  const employeesInProbation = employees.filter(e => {
    if (!e.dateOfJoining || !e.probationPeriod) return false;
    const joinDate = new Date(e.dateOfJoining);
    const probationEndDate = new Date(joinDate);
    probationEndDate.setMonth(probationEndDate.getMonth() + e.probationPeriod);
    return now >= joinDate && now <= probationEndDate && e.status === 'active';
  }).length;

  const statCards = [
    {
      label: 'Total Organizations',
      value: stats?.totalOrganizations || organizations.length,
      icon: FiBriefcase,
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      trend: `${stats?.activeOrganizations || organizations.filter(o => o.status === 'active').length} active`,
      positive: true
    },
    {
      label: timeFilter === 'day' ? 'Today\'s Employees' : timeFilter === 'week' ? 'This Week' : timeFilter === 'month' ? 'This Month' : 'This Year',
      value: filteredEmployees.length,
      icon: FiUsers,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      trend: `${activeEmployees} active`,
      positive: true
    },
    {
      label: 'Total Locations',
      value: stats?.totalLocations || 0,
      icon: FiMapPin,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      trend: `${stats?.activeLocations || 0} active`,
      positive: true
    },
    {
      label: 'New This Week',
      value: employeesThisWeek,
      icon: FiUserPlus,
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      trend: 'employees added',
      positive: true
    }
  ];

  // Chart data: Employee status distribution
  const statusChartData = [
    { name: 'Active', value: activeEmployees, color: '#10b981' },
    { name: 'Inactive', value: inactiveEmployees, color: '#f59e0b' },
    { name: 'Terminated', value: terminatedEmployees, color: '#ef4444' }
  ];

  // Chart data: Employees per organization (using filtered data)
  const orgEmployeeCounts = organizations.map(org => ({
    name: org.name.length > 15 ? org.name.substring(0, 15) + '...' : org.name,
    employees: filteredEmployees.filter(e => e.orgId === org.orgId).length
  })).filter(org => org.employees > 0).sort((a, b) => b.employees - a.employees).slice(0, 10);

  // Chart data: Employee additions over last 6 months
  const last6Months = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (5 - i));
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear(),
      fullDate: new Date(date.getFullYear(), date.getMonth(), 1)
    };
  });

  const employeeGrowthData = last6Months.map(({ month, year, fullDate }) => {
    const nextMonth = new Date(fullDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const count = employees.filter(e => {
      const createdDate = new Date(e.createdAt);
      const isInRange = createdDate >= fullDate && createdDate < nextMonth;

      // Debug logging for August and September
      if (month === 'Aug' || month === 'Sep') {
        console.log(`${month} ${year}: Employee ${e.name}, Created: ${e.createdAt}, Parsed: ${createdDate.toISOString()}, Range: ${fullDate.toISOString()} - ${nextMonth.toISOString()}, In Range: ${isInRange}`);
      }

      return isInRange;
    }).length;

    console.log(`${month} ${year}: ${count} employees`);

    return {
      name: `${month} ${year}`,
      employees: count
    };
  });

  return (
    <DashboardContainer>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>Dashboard Overview</PageTitle>
          <PageSubtitle>Monitor your system performance and analytics</PageSubtitle>
        </HeaderLeft>
        <HeaderRight>
          <TimeFilter>
            <FilterButton active={timeFilter === 'day'} onClick={() => setTimeFilter('day')}>
              Today
            </FilterButton>
            <FilterButton active={timeFilter === 'week'} onClick={() => setTimeFilter('week')}>
              Week
            </FilterButton>
            <FilterButton active={timeFilter === 'month'} onClick={() => setTimeFilter('month')}>
              Month
            </FilterButton>
            <FilterButton active={timeFilter === 'year'} onClick={() => setTimeFilter('year')}>
              Year
            </FilterButton>
          </TimeFilter>
        </HeaderRight>
      </PageHeader>

      <StatsGrid>
        {statCards.map((card, index) => (
          <StatCardNew key={index} gradient={card.gradient}>
            <StatCardHeader>
              <StatIconWrapper bgColor={card.color}>
                <card.icon />
              </StatIconWrapper>
            </StatCardHeader>
            <StatInfo>
              <StatLabel>{card.label}</StatLabel>
              <StatValue>{card.value.toLocaleString()}</StatValue>
              <StatTrend positive={card.positive}>
                {card.trend}
              </StatTrend>
            </StatInfo>
          </StatCardNew>
        ))}
      </StatsGrid>

      <Section>
        <SectionTitle>Quick Insights</SectionTitle>
        <InsightsGrid>
          <InsightCard>
            <div className="insight-header">
              <FiClock />
              <div className="insight-title">Probation Period</div>
            </div>
            <div className="insight-description">
              {employeesInProbation} employees currently in probation period
            </div>
          </InsightCard>

          <InsightCard warning={employeesWithoutDocs > 0}>
            <div className="insight-header">
              <FiFileText />
              <div className="insight-title">Document Compliance</div>
            </div>
            <div className="insight-description">
              {employeesWithoutDocs > 0
                ? `${employeesWithoutDocs} employees missing documents`
                : 'All employees have documents uploaded'}
            </div>
          </InsightCard>

          <InsightCard>
            <div className="insight-header">
              <FiActivity />
              <div className="insight-title">Employee Status</div>
            </div>
            <div className="insight-description">
              {activeEmployees} active · {inactiveEmployees} inactive · {terminatedEmployees} terminated
            </div>
          </InsightCard>

          <InsightCard warning={inactiveEmployees > activeEmployees * 0.2}>
            <div className="insight-header">
              <FiAlertCircle />
              <div className="insight-title">Retention Rate</div>
            </div>
            <div className="insight-description">
              {((activeEmployees / (activeEmployees + inactiveEmployees + terminatedEmployees)) * 100).toFixed(1)}% active retention rate
            </div>
          </InsightCard>
        </InsightsGrid>
      </Section>

      <Section>
        <SectionTitle>Recent Activity</SectionTitle>
        <ActivityFeed>
          {filteredEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              No employee activity in this time period
            </div>
          ) : (
            filteredEmployees
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .slice(0, 10)
              .map((employee, index) => {
                const timeAgo = getTimeAgo(new Date(employee.createdAt));
                const orgName = employee.orgName || organizations.find(o => o.orgId === employee.orgId)?.name || 'Unknown Org';

                return (
                  <ActivityItem key={employee.id || index}>
                    <div className="activity-icon">
                      <FiUserPlus />
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{employee.name}</div>
                      <div className="activity-description">
                        Joined {orgName} · {employee.designation || 'Employee'}
                      </div>
                    </div>
                    <div className="activity-time">{timeAgo}</div>
                  </ActivityItem>
                );
              })
          )}
        </ActivityFeed>
      </Section>

      <Section>
        <SectionTitle>Analytics & Insights</SectionTitle>
        <ChartsGrid>
          <ChartCard>
            <ChartTitle>Employee Status Distribution</ChartTitle>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Top 10 Organizations by Employees</ChartTitle>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={orgEmployeeCounts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="employees" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard style={{ gridColumn: 'span 2' }}>
            <ChartTitle>Employee Growth (Last 6 Months)</ChartTitle>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={employeeGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="employees"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsGrid>
      </Section>

      <Section>
        <SectionTitle>Quick Actions</SectionTitle>
        <QuickAccessGrid>
          <QuickAccessCard href="/employees">
            <div className="icon-wrapper">
              <FiUsers />
            </div>
            <div className="card-content">
              <div className="card-title">View Employees</div>
              <div className="card-subtitle">Manage all employees</div>
            </div>
          </QuickAccessCard>

          <QuickAccessCard href="/onboarding">
            <div className="icon-wrapper">
              <FiUserPlus />
            </div>
            <div className="card-content">
              <div className="card-title">Add Employee</div>
              <div className="card-subtitle">Start onboarding process</div>
            </div>
          </QuickAccessCard>

          <QuickAccessCard href="/map">
            <div className="icon-wrapper">
              <FiMapPin />
            </div>
            <div className="card-content">
              <div className="card-title">View Map</div>
              <div className="card-subtitle">Explore locations on globe</div>
            </div>
          </QuickAccessCard>

          <QuickAccessCard href="/organizations">
            <div className="icon-wrapper">
              <FiBriefcase />
            </div>
            <div className="card-content">
              <div className="card-title">Organizations</div>
              <div className="card-subtitle">Manage organizations</div>
            </div>
          </QuickAccessCard>
        </QuickAccessGrid>
      </Section>
    </DashboardContainer>
  );
};

// Helper function to calculate time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
